/**
 * Utilitarios do mapa de calor: carregamento dos contornos, casamento com as
 * areas vindas da API e escala de cores.
 *
 * Contornos (public/geo/): bairros de Manaus dissolvidos da malha de setores
 * censitarios do IBGE (Censo 2022) e municipios da malha municipal do IBGE.
 * Ambos ja vem com a propriedade "chave" resolvida contra a base (nm_bairro do
 * TSE / municipio.id), entao aqui nao ha de-para de nomes em runtime — apenas um
 * fallback por nome normalizado, para o caso de os ids de municipio diferirem
 * entre instancias.
 *
 * A malha do IBGE cobre 100% do territorio: todo bairro do TSE tem contorno. A
 * auditoria de areas sem poligono continua no componente como rede de seguranca
 * caso o TSE mude nomes numa atualizacao futura.
 */
import axios from "axios";

export const URL_BAIRROS = "geo/manaus-bairros.json";
export const URL_MUNICIPIOS = "geo/am-municipios.json";

/** Maiusculas, sem acento e sem pontuacao. */
export function normalizar(texto) {
  if (!texto) return "";
  const semAcento = String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return semAcento
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

/** Busca um GeoJSON de public/geo/ (axios: core-js@2 nao polyfilla fetch). */
export function carregarGeoJson(arquivo) {
  const base = process.env.BASE_URL || "/";
  return axios.get(base + arquivo).then((r) => r.data);
}

/**
 * Indexa as areas da API por chave e por nome normalizado, para o painter
 * resolver cada feature do GeoJSON.
 */
export function indexarAreas(areas) {
  const porChave = {};
  const porNome = {};
  (areas || []).forEach((a) => {
    porChave[String(a.chave)] = a;
    porNome[normalizar(a.nome)] = a;
  });
  return { porChave, porNome };
}

/** Resolve a area correspondente a um poligono. */
export function areaDoFeature(feature, indice) {
  const props = feature && feature.properties ? feature.properties : {};
  const chave = props.chave;
  if (chave !== null && chave !== undefined && indice.porChave[String(chave)]) {
    return indice.porChave[String(chave)];
  }
  const porNome = indice.porNome[normalizar(props.nome)];
  return porNome || null;
}

/**
 * Quebras por quantil. Escala linear nao serve: a distribuicao e muito
 * assimetrica (Manaus concentra ~77% dos cadastros do estado) e deixaria quase
 * todas as areas na mesma cor.
 */
export function calcularQuebras(valores, classes) {
  const n = classes || 5;
  const ordenados = (valores || [])
    .filter((v) => v !== null && v !== undefined && !isNaN(v))
    .sort((a, b) => a - b);

  if (!ordenados.length) return [];

  const quebras = [];
  for (let i = 1; i < n; i += 1) {
    const pos = (ordenados.length - 1) * (i / n);
    const base = Math.floor(pos);
    const resto = pos - base;
    const valor =
      base + 1 < ordenados.length
        ? ordenados[base] + resto * (ordenados[base + 1] - ordenados[base])
        : ordenados[base];
    quebras.push(valor);
  }
  // Quantis repetidos (muitas areas com o mesmo valor) viram uma classe so.
  return quebras.filter((v, i, arr) => i === 0 || v > arr[i - 1]);
}

/** Rampa sequencial (claro = frio, escuro = quente). */
export const RAMPA = ["#fee5d9", "#fcae91", "#fb6a4a", "#de2d26", "#a50f15"];
export const COR_SEM_DADO = "#dfe3e8";

export function corPorQuebras(valor, quebras) {
  if (valor === null || valor === undefined || isNaN(valor)) return COR_SEM_DADO;
  let i = 0;
  while (i < quebras.length && valor > quebras[i]) i += 1;
  const passo = Math.max(1, Math.ceil(RAMPA.length / (quebras.length + 1)));
  return RAMPA[Math.min(RAMPA.length - 1, i * passo)];
}
